/* =========================================================================
   state.tsx — Scenario state (React context) with undo/redo + Supabase sync.
   Split into 4 bounded contexts for performance.
   ========================================================================= */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import type {
  Scenario,
  ModelResult,
  Building,
  BuilderQuote,
  ActionItem,
  AuditEntry,
} from "./types";
import type { Site } from "./platform-types";
import type { MonteCarloResult } from "./model";
import { Model } from "./model";
import {
  loadPersisted,
  savePersisted,
  removeScenario,
  syncBuilderQuotes,
  migrateScenarioBlob,
  saveVersion as saveVersionPersist,
  capAuditLog,
  loadSites,
  saveSitesLocal,
  syncQueue,
} from "./persistence";
import { toastGlobal } from "./toast";
import { useAuth } from "./auth-context";
import { computeFingerprint } from "./platform-types";
import { createSite, updateSite, removeSite } from "./sites";
import { useRealtimeScenarios } from "./ws-sync/use-realtime";
import { resolveConflict } from "./ws-sync/conflict-resolver";
import { isSupabaseConfigured } from "./supabase-client";
import { SHARED_WORKSPACE_NOTICE } from "./security-disclosure";

/* ------------------------------------------------------------------ */
//  Legacy composite interface (backward-compatible)
/* ------------------------------------------------------------------ */
export interface ScenarioContextValue {
  scenarios: Scenario[];
  activeId: string;
  active: Scenario;
  result: ModelResult;
  results: Record<string, ModelResult>;
  setActiveId: (id: string) => void;
  sites: Site[];
  activeSiteId: string | null;
  setActiveSiteId: (id: string | null) => void;
  updateAssumption: (key: string, value: unknown) => void;
  updateBuilding: (id: string, patch: Partial<Building>) => void;
  addBuilding: () => void;
  removeBuilding: (id: string) => void;
  resetActiveToDefault: () => void;
  pushSharedToAll: () => void;
  cloneActiveAs: (name: string) => void;
  deleteScenario: (id: string) => void;
  exportScenario: () => string;
  importScenario: (json: string) => void;
  benchmarkBuilding: Building | null;
  saveBenchmarkBuilding: (id: string) => void;
  saveCustomBenchmarkBuilding: (b: Omit<Building, "id">) => void;
  clearBenchmarkBuilding: () => void;
  addBuildingFromBenchmark: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  loading: boolean;
  mcResult: MonteCarloResult | null;
  mcHash: string;
  setMcResult: (result: MonteCarloResult | null, hash?: string) => void;
  setBuilderQuotes: (quotes: BuilderQuote[]) => void;
  setActionItems: (items: ActionItem[]) => void;
  saveVersion: (label: string, note?: string, createdByLabel?: string) => void;
  restoreVersion: (version: import("./platform-types").ScenarioVersion) => void;
  updateCatchment: (code: string, area?: Scenario["catchment"]) => void;
  addSite: (name: string, description?: string) => string;
  renameSite: (id: string, name: string, description?: string) => void;
  deleteSite: (id: string) => void;
  setScenarioSite: (scenarioId: string, siteId: string | null) => void;
}

/* ------------------------------------------------------------------ */
//  Bounded context interfaces
/* ------------------------------------------------------------------ */
export interface ScenarioDataContextValue {
  scenarios: Scenario[];
  activeId: string;
  active: Scenario;
  result: ModelResult;
  results: Record<string, ModelResult>;
  setActiveId: (id: string) => void;
  sites: Site[];
  activeSiteId: string | null;
  setActiveSiteId: (id: string | null) => void;
}

export interface ScenarioActionsContextValue {
  updateAssumption: (key: string, value: unknown) => void;
  updateBuilding: (id: string, patch: Partial<Building>) => void;
  addBuilding: () => void;
  removeBuilding: (id: string) => void;
  resetActiveToDefault: () => void;
  pushSharedToAll: () => void;
  cloneActiveAs: (name: string) => void;
  deleteScenario: (id: string) => void;
  exportScenario: () => string;
  importScenario: (json: string) => void;
  saveBenchmarkBuilding: (id: string) => void;
  saveCustomBenchmarkBuilding: (b: Omit<Building, "id">) => void;
  clearBenchmarkBuilding: () => void;
  addBuildingFromBenchmark: () => void;
  setBuilderQuotes: (quotes: BuilderQuote[]) => void;
  setActionItems: (items: ActionItem[]) => void;
  saveVersion: (label: string, note?: string, createdByLabel?: string) => void;
  restoreVersion: (version: import("./platform-types").ScenarioVersion) => void;
  updateCatchment: (code: string, area?: Scenario["catchment"]) => void;
  addSite: (name: string, description?: string) => string;
  renameSite: (id: string, name: string, description?: string) => void;
  deleteSite: (id: string) => void;
  setScenarioSite: (scenarioId: string, siteId: string | null) => void;
}

export interface ScenarioUIContextValue {
  benchmarkBuilding: Building | null;
  loading: boolean;
  mcResult: MonteCarloResult | null;
  mcHash: string;
  setMcResult: (result: MonteCarloResult | null, hash?: string) => void;
}

export interface HistoryContextValue {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

function clonePlanEvidence(
  source: Scenario,
  target: Scenario,
): unknown[] | undefined {
  const sameSite = (source.siteId ?? null) === (target.siteId ?? null);
  const evidenceOwner = sameSite ? source : target;
  const evidence = (evidenceOwner as unknown as Record<string, unknown>)
    .planEvidence;
  if (Array.isArray(evidence)) return structuredClone(evidence);
  return sameSite ? [] : undefined;
}

/* ------------------------------------------------------------------ */
//  Contexts
/* ------------------------------------------------------------------ */
const ScenarioDataContext = createContext<ScenarioDataContextValue | null>(
  null,
);
const ScenarioActionsContext =
  createContext<ScenarioActionsContextValue | null>(null);
const ScenarioUIContext = createContext<ScenarioUIContextValue | null>(null);
const HistoryContext = createContext<HistoryContextValue | null>(null);

const MAX_HISTORY = 50;

interface HistoryEntry {
  scenarios: Scenario[];
  activeId: string;
}

export function ScenarioProvider({ children }: { children: React.ReactNode }) {
  // loop#034: the product seeds the RevG-basis production set; defaultScenarios()
  // remains the legacy engine anchor for the pinned test corpus.
  const defaults = useMemo(() => Model.productionScenarios(), []);

  const [scenarios, setScenarios] = useState<Scenario[]>(defaults);
  const [activeId, setActiveId] = useState("base");
  const [sites, setSites] = useState<Site[]>([]);
  const [activeSiteId, setActiveSiteIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [benchmarkBuilding, setBenchmarkBuilding] = useState<Building | null>(
    null,
  );
  const [mcResult, setMcResultState] = useState<MonteCarloResult | null>(null);
  const [mcHash, setMcHash] = useState("");
  const firstMount = useRef(true);
  const loadedRef = useRef(false);

  // ---- per-scenario result cache ----
  // Only recomputes scenarios whose object reference changed (immutable updates)
  const resultCache = useRef<
    Map<string, { scenario: Scenario; result: ModelResult }>
  >(new Map());

  // ---- initial load from persistence ----
  useEffect(() => {
    if (isSupabaseConfigured()) {
      console.info(`[security] ${SHARED_WORKSPACE_NOTICE}`);
    }
    let cancelled = false;
    Promise.all([loadPersisted(), loadSites()])
      .then(([state, loadedSites]) => {
        if (cancelled) return;
        if (state) {
          // Migrate persisted scenarios to the current assumption schema (older saves may lack
          // newer fields, e.g. preliminariesRate/builderMarginRate/schoolSpecTier → NaN if not filled).
          setScenarios(state.scenarios.map(Model.normalizeScenario));
          setActiveId(state.activeId);
        }
        setSites(loadedSites ?? []);
        loadedRef.current = true;
        // e2e/fixtures.ts:expectVisibleAppShell waits on this flag by name — keep in sync.
        (
          globalThis as unknown as Record<string, unknown>
        ).__horizonPersistenceReady = true;
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          loadedRef.current = true;
          // e2e/fixtures.ts:expectVisibleAppShell waits on this flag by name — keep in sync.
          (
            globalThis as unknown as Record<string, unknown>
          ).__horizonPersistenceReady = true;
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- undo/redo history ----
  const [history, setHistory] = useState<HistoryEntry[]>([
    { scenarios: structuredClone(defaults), activeId: "base" },
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyIndexRef = useRef(historyIndex);
  historyIndexRef.current = historyIndex;

  // Reset history when initial load completes
  useEffect(() => {
    if (loadedRef.current && !firstMount.current) {
      setHistory([{ scenarios: structuredClone(scenarios), activeId }]);
      setHistoryIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedRef.current]);

  const pushHistory = useCallback(
    (nextScenarios: Scenario[], nextActiveId: string) => {
      setHistory((prev) => {
        const idx = historyIndexRef.current;
        const trimmed = prev.slice(0, idx + 1);
        const entry: HistoryEntry = {
          scenarios: structuredClone(nextScenarios),
          activeId: nextActiveId,
        };
        const next = [...trimmed, entry];
        if (next.length > MAX_HISTORY) next.shift();
        return next;
      });
      setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
    },
    [],
  );

  const undo = useCallback(() => {
    const prev = historyIndexRef.current;
    if (prev <= 0) return;
    const idx = prev - 1;
    const entry = history[idx];
    if (!entry) return;
    setScenarios(structuredClone(entry.scenarios));
    setActiveId(entry.activeId);
    setHistoryIndex(idx);
    toastGlobal("Undone", "info", 1800);
  }, [history]);

  const redo = useCallback(() => {
    const prev = historyIndexRef.current;
    if (prev >= history.length - 1) return;
    const idx = prev + 1;
    const entry = history[idx];
    if (!entry) return;
    setScenarios(structuredClone(entry.scenarios));
    setActiveId(entry.activeId);
    setHistoryIndex(idx);
    toastGlobal("Redone", "info", 1800);
  }, [history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "z" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (e.key.toLowerCase() === "y" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        redo();
      }
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const active = scenarios.find((s) => s.id === activeId) || scenarios[0];
  if (!active) throw new Error("Invariant: at least one scenario required");

  const updateAssumption = useCallback(
    (key: string, value: unknown) => {
      setScenarios((prev) => {
        const oldScenario = prev.find((s) => s.id === activeId);
        const oldValue = oldScenario
          ? (oldScenario.assumptions as unknown as Record<string, unknown>)[key]
          : undefined;
        const entry: import("./types").AuditEntry = {
          field: key,
          oldValue,
          newValue: value,
          timestamp: Date.now(),
        };
        const next = prev.map((s) =>
          s.id === activeId
            ? {
                ...s,
                assumptions: {
                  ...s.assumptions,
                  [key]: value,
                } as Scenario["assumptions"],
                auditLog: [...(s.auditLog || []), entry],
              }
            : s,
        );
        pushHistory(next, activeId);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const updateCatchment = useCallback(
    (code: string, area?: Scenario["catchment"]) => {
      setScenarios((prev) => {
        const oldScenario = prev.find((s) => s.id === activeId);
        const oldValue = oldScenario
          ? oldScenario.assumptions.catchmentCode
          : undefined;
        const entry: AuditEntry = {
          field: "catchmentCode",
          oldValue,
          newValue: code,
          timestamp: Date.now(),
        };
        const next = prev.map((s) =>
          s.id === activeId
            ? {
                ...s,
                assumptions: {
                  ...s.assumptions,
                  catchmentCode: code,
                } as Scenario["assumptions"],
                catchment: area,
                auditLog: [...(s.auditLog || []), entry],
              }
            : s,
        );
        pushHistory(next, activeId);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const updateBuilding = useCallback(
    (id: string, patch: Partial<Building>) => {
      setScenarios((prev) => {
        const next = prev.map((s) =>
          s.id === activeId
            ? {
                ...s,
                buildings: s.buildings.map((b) =>
                  b.id === id ? { ...b, ...patch } : b,
                ),
              }
            : s,
        );
        pushHistory(next, activeId);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const addBuilding = useCallback(() => {
    setScenarios((prev) => {
      const next = prev.map((s) => {
        if (s.id !== activeId) return s;
        const bld: Building = {
          id: "b" + Date.now(),
          name: `Building ${s.buildings.length + 1}`,
          nlaSqm: 1500,
          gfaSqm: 4500,
          constructionPerSqmGfa: 1500,
          reddamCapex: 1_000_000,
          netRentPerSqm: 650,
          outgoingsPerSqm: 120,
          enrolmentCapacity: 100,
          comesOnlineYear: 1,
        };
        return { ...s, buildings: [...s.buildings, bld] };
      });
      pushHistory(next, activeId);
      return next;
    });
  }, [activeId, pushHistory]);

  const removeBuilding = useCallback(
    (id: string) => {
      setScenarios((prev) => {
        const next = prev.map((s) =>
          s.id === activeId
            ? { ...s, buildings: s.buildings.filter((b) => b.id !== id) }
            : s,
        );
        pushHistory(next, activeId);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const resetActiveToDefault = useCallback(() => {
    setScenarios((prev) => {
      const next = prev.map((s) => {
        if (s.id !== activeId) return s;
        const fresh = Model.productionScenarios().find(
          (d) => d.id === activeId,
        );
        return fresh ? structuredClone(fresh) : s;
      });
      pushHistory(next, activeId);
      return next;
    });
  }, [activeId, pushHistory]);

  // Copy the active scenario's SHARED inputs (buildings, same-site plan evidence, and
  // all non-lever assumptions: land, construction/debt rates, fee config, etc.) into
  // every other scenario, leaving each scenario's own levers (ramp, fees, cap rate,
  // growth, fitout — Model.SCENARIO_LEVER_KEYS) untouched. Cross-site evidence remains
  // with its target site; absent same-site evidence becomes an empty serializable
  // tombstone so realtime clients clear it. Undoable via history.
  const pushSharedToAll = useCallback(() => {
    setScenarios((prev) => {
      const src = prev.find((s) => s.id === activeId);
      if (!src || prev.length < 2) return prev;
      const levers = new Set<string>(
        Model.SCENARIO_LEVER_KEYS as readonly string[],
      );
      const next = prev.map((s) => {
        if (s.id === activeId) return s;
        const mergedAssumptions = { ...src.assumptions };
        const target = mergedAssumptions as unknown as Record<string, unknown>;
        const own = s.assumptions as unknown as Record<string, unknown>;
        for (const k of levers) target[k] = own[k];
        return {
          ...s,
          assumptions: mergedAssumptions,
          buildings: structuredClone(src.buildings),
          planEvidence: clonePlanEvidence(src, s),
        };
      });
      pushHistory(next, activeId);
      return next;
    });
  }, [activeId, pushHistory]);

  const cloneActiveAs = useCallback(
    (name: string) => {
      setScenarios((prev) => {
        const cur = prev.find((s) => s.id === activeId);
        if (!cur) return prev;
        const id = "cust-" + Date.now();
        const copy: Scenario = structuredClone(cur);
        copy.id = id;
        copy.name = name;
        copy.description = "Custom scenario.";
        const next = [...prev, copy];
        pushHistory(next, id);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const saveBenchmarkBuilding = useCallback(
    (id: string) => {
      const b = active.buildings.find((bb) => bb.id === id);
      if (b) setBenchmarkBuilding(structuredClone(b));
    },
    [active],
  );

  const saveCustomBenchmarkBuilding = useCallback((b: Omit<Building, "id">) => {
    setBenchmarkBuilding({ ...b, id: "bench-custom-" + Date.now() });
  }, []);

  const clearBenchmarkBuilding = useCallback(() => {
    setBenchmarkBuilding(null);
  }, []);

  const addBuildingFromBenchmark = useCallback(() => {
    setScenarios((prev) => {
      const next = prev.map((s) => {
        if (s.id !== activeId) return s;
        const ref = benchmarkBuilding || s.buildings[0];
        if (!ref) return s;
        const copy: Building = {
          ...structuredClone(ref),
          id: "b" + Date.now(),
          name: `${ref.name} (option ${s.buildings.length + 1})`,
        };
        return { ...s, buildings: [...s.buildings, copy] };
      });
      pushHistory(next, activeId);
      return next;
    });
  }, [activeId, benchmarkBuilding, pushHistory]);

  const deleteScenario = useCallback(
    (id: string) => {
      setScenarios((prev) => {
        const next = prev.filter((s) => s.id !== id);
        if (next.length === 0) return prev; // never delete the last scenario
        let nextActiveId = activeId;
        if (activeId === id) {
          nextActiveId = next[0]?.id ?? activeId;
          setActiveId(nextActiveId);
        }
        pushHistory(next, nextActiveId);
        return next;
      });
      removeScenario(id).catch(() => {});
    },
    [activeId, pushHistory],
  );

  const exportScenario = useCallback(() => {
    const payload = {
      version: 1,
      exportedAt: new Date().toISOString(),
      scenario: structuredClone(active),
    };
    return JSON.stringify(payload, null, 2);
  }, [active]);

  const importScenario = useCallback(
    (json: string) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        throw new Error("Invalid JSON file.");
      }
      if (!parsed || typeof parsed !== "object")
        throw new Error("Invalid JSON file.");
      const o = parsed as Record<string, unknown>;
      const raw = o.scenario ?? o;
      if (
        !raw ||
        typeof raw !== "object" ||
        !("id" in raw) ||
        !("name" in raw) ||
        !("assumptions" in raw) ||
        !("buildings" in raw)
      ) {
        throw new Error("File does not contain a valid scenario.");
      }
      let imported: Scenario = Model.normalizeScenario(
        structuredClone(raw as Scenario),
      );
      imported = migrateScenarioBlob(imported);
      imported.id = "cust-import-" + Date.now();
      const importedName = imported.name.trim();
      imported.name = /\s\(imported\)$/i.test(importedName)
        ? importedName
        : `${importedName} (imported)`;
      setScenarios((prev) => {
        const next = [...prev, imported];
        pushHistory(next, imported.id);
        return next;
      });
      setActiveId(imported.id);
    },
    [pushHistory],
  );

  // Auto-save to persistence (skip first mount since we just loaded)
  useEffect(() => {
    if (firstMount.current) {
      firstMount.current = false;
      return;
    }
    if (!loadedRef.current) return;
    // Stamp last-write time so the conflict resolver can do honest last-write-wins (§4).
    const stamped = scenarios.map((s) => ({ ...s, updatedAt: Date.now() }));
    const ok = savePersisted(stamped, activeId);
    if (!ok) {
      toastGlobal(
        "Failed to save scenario. Storage may be full.",
        "warning",
        6000,
      );
    }
  }, [scenarios, activeId]);

  // Realtime sync: merge remote scenario changes
  const handleRealtimeChange = useCallback((payload: unknown) => {
    if (!payload || typeof payload !== "object") return;
    const p = payload as Record<string, unknown>;
    const eventType = p.eventType as string;

    if (eventType === "DELETE") {
      const oldId = (p.old as Record<string, unknown>)?.id as string;
      if (oldId) {
        setScenarios((prev) => prev.filter((s) => s.id !== oldId));
      }
      return;
    }

    const remoteData = p.new as Record<string, unknown>;
    if (!remoteData?.data) return;
    const remoteScenario = remoteData.data as Scenario;

    setScenarios((prev) => {
      const local = prev.find((s) => s.id === remoteScenario.id);
      if (!local) {
        return [...prev, remoteScenario];
      }
      const resolution = resolveConflict(local, remoteScenario);
      if (resolution.winner === remoteScenario) {
        return prev.map((s) =>
          s.id === remoteScenario.id ? { ...s, ...remoteScenario } : s,
        );
      }
      return prev;
    });
  }, []);

  const { user } = useAuth();

  useRealtimeScenarios(handleRealtimeChange);

  const results = useMemo(() => {
    const r: Record<string, ModelResult> = {};
    for (const s of scenarios) {
      const cached = resultCache.current.get(s.id);
      if (cached && cached.scenario === s) {
        r[s.id] = cached.result;
      } else {
        const computed = Model.compute(s);
        resultCache.current.set(s.id, { scenario: s, result: computed });
        r[s.id] = computed;
      }
    }
    // Evict stale entries for deleted scenarios
    const activeIds = new Set(scenarios.map((s) => s.id));
    for (const id of Array.from(resultCache.current.keys())) {
      if (!activeIds.has(id)) resultCache.current.delete(id);
    }
    return r;
  }, [scenarios]);
  const result = results[active.id];

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const setMcResult = useCallback(
    (result: MonteCarloResult | null, hash?: string) => {
      setMcResultState(result);
      if (hash !== undefined) setMcHash(hash);
    },
    [],
  );

  const setBuilderQuotes = useCallback(
    (quotes: BuilderQuote[]) => {
      setScenarios((prev) => {
        const next = prev.map((s) =>
          s.id === activeId ? { ...s, builderQuotes: quotes } : s,
        );
        pushHistory(next, activeId);
        return next;
      });
      syncBuilderQuotes(activeId, quotes).catch(() => {});
    },
    [activeId, pushHistory],
  );

  const setActionItems = useCallback(
    (items: ActionItem[]) => {
      setScenarios((prev) => {
        const next = prev.map((s) =>
          s.id === activeId ? { ...s, actionItems: items } : s,
        );
        pushHistory(next, activeId);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const saveVersion = useCallback(
    (label: string, note?: string, createdByLabel?: string) => {
      const active = scenarios.find((s) => s.id === activeId);
      if (!active) return;
      const snapshot = structuredClone(active);
      snapshot.auditLog = capAuditLog(snapshot.auditLog);
      const result = results[active.id] ?? Model.compute(snapshot);
      const fingerprint = computeFingerprint(result);
      saveVersionPersist({
        scenarioId: active.id,
        label,
        note,
        snapshot,
        schemaVersion: snapshot.schemaVersion ?? 1,
        computeFingerprint: fingerprint,
        createdByLabel: createdByLabel ?? user?.email ?? undefined,
      });
    },
    [scenarios, activeId, results, user],
  );

  const restoreVersion = useCallback(
    (version: import("./platform-types").ScenarioVersion) => {
      const restored = {
        ...structuredClone(version.snapshot),
        id: version.scenarioId,
        updatedAt: Date.now(),
      };
      const next = scenarios.map((s) =>
        s.id === version.scenarioId ? restored : s,
      );
      setScenarios(next);
      pushHistory(next, version.scenarioId);
      setActiveId(version.scenarioId);
      // Capture the restored state as a governance record. We call the persistence seam
      // directly (instead of the saveVersion action) so the snapshot is the restored blob,
      // not the pre-restore state captured by a stale closure.
      saveVersionPersist({
        scenarioId: version.scenarioId,
        label: `Restored: ${version.label}`,
        snapshot: restored,
        schemaVersion: restored.schemaVersion ?? 1,
        createdByLabel: version.createdByLabel,
      });
    },
    [scenarios, pushHistory],
  );

  // ---- site actions (organizing layer — not part of scenario undo/redo) ----
  const addSite = useCallback((name: string, description?: string) => {
    const site = createSite(name, description);
    setSites((prev) => {
      const next = [...prev, site];
      saveSitesLocal(next);
      return next;
    });
    syncQueue.enqueue("site", site.id, site);
    return site.id;
  }, []);

  const renameSite = useCallback(
    (id: string, name: string, description?: string) => {
      setSites((prev) => {
        const next = updateSite(prev, id, name, description);
        const updated = next.find((s) => s.id === id);
        if (updated) {
          saveSitesLocal(next);
          syncQueue.enqueue("site", id, updated);
        }
        return next;
      });
    },
    [],
  );

  const deleteSite = useCallback(
    (id: string) => {
      setSites((prev) => {
        const next = removeSite(prev, id);
        saveSitesLocal(next);
        syncQueue.enqueue("site", id, { id, _deleted: true });
        return next;
      });
      // Orphan scenarios: clear siteId for all scenarios assigned to this site.
      setScenarios((prev) => {
        const next = prev.map((s) =>
          s.siteId === id ? { ...s, siteId: undefined } : s,
        );
        pushHistory(
          next,
          activeId === id ? (next[0]?.id ?? activeId) : activeId,
        );
        return next;
      });
      if (activeSiteId === id) setActiveSiteIdState(null);
    },
    [activeId, activeSiteId, pushHistory],
  );

  const setScenarioSite = useCallback(
    (scenarioId: string, siteId: string | null) => {
      setScenarios((prev) => {
        const oldScenario = prev.find((s) => s.id === scenarioId);
        const oldValue = oldScenario?.siteId ?? null;
        if (oldValue === siteId) return prev;
        const entry: import("./types").AuditEntry = {
          field: "siteId",
          oldValue,
          newValue: siteId,
          timestamp: Date.now(),
        };
        const next = prev.map((s) =>
          s.id === scenarioId
            ? {
                ...s,
                siteId: siteId ?? undefined,
                auditLog: [...(s.auditLog || []), entry],
              }
            : s,
        );
        pushHistory(next, activeId);
        return next;
      });
    },
    [activeId, pushHistory],
  );

  const setActiveSiteId = useCallback(
    (id: string | null) => {
      setActiveSiteIdState(id);
      // If the active scenario is filtered out, re-point to the first visible scenario.
      if (id !== null) {
        setActiveId((prev) => {
          const activeScenario = scenarios.find((s) => s.id === prev);
          if (activeScenario && activeScenario.siteId === id) return prev;
          const firstVisible = scenarios.find((s) => s.siteId === id);
          return firstVisible?.id ?? prev;
        });
      }
    },
    [scenarios],
  );

  // ---- bounded context values ----
  const dataValue = useMemo<ScenarioDataContextValue>(
    () => ({
      scenarios,
      activeId,
      active,
      result,
      results,
      setActiveId,
      sites,
      activeSiteId,
      setActiveSiteId,
    }),
    [
      scenarios,
      activeId,
      active,
      result,
      results,
      setActiveId,
      sites,
      activeSiteId,
      setActiveSiteId,
    ],
  );

  const actionsValue = useMemo<ScenarioActionsContextValue>(
    () => ({
      updateAssumption,
      updateCatchment,
      updateBuilding,
      addBuilding,
      removeBuilding,
      resetActiveToDefault,
      pushSharedToAll,
      cloneActiveAs,
      deleteScenario,
      exportScenario,
      importScenario,
      saveBenchmarkBuilding,
      saveCustomBenchmarkBuilding,
      clearBenchmarkBuilding,
      addBuildingFromBenchmark,
      setBuilderQuotes,
      setActionItems,
      saveVersion,
      restoreVersion,
      addSite,
      renameSite,
      deleteSite,
      setScenarioSite,
    }),
    [
      updateAssumption,
      updateCatchment,
      updateBuilding,
      addBuilding,
      removeBuilding,
      resetActiveToDefault,
      pushSharedToAll,
      cloneActiveAs,
      deleteScenario,
      exportScenario,
      importScenario,
      saveBenchmarkBuilding,
      saveCustomBenchmarkBuilding,
      clearBenchmarkBuilding,
      addBuildingFromBenchmark,
      setBuilderQuotes,
      setActionItems,
      saveVersion,
      restoreVersion,
      addSite,
      renameSite,
      deleteSite,
      setScenarioSite,
    ],
  );

  const uiValue = useMemo<ScenarioUIContextValue>(
    () => ({
      benchmarkBuilding,
      loading,
      mcResult,
      mcHash,
      setMcResult,
    }),
    [benchmarkBuilding, loading, mcResult, mcHash, setMcResult],
  );

  const historyValue = useMemo<HistoryContextValue>(
    () => ({
      canUndo,
      canRedo,
      undo,
      redo,
    }),
    [canUndo, canRedo, undo, redo],
  );

  return (
    <ScenarioDataContext.Provider value={dataValue}>
      <ScenarioActionsContext.Provider value={actionsValue}>
        <ScenarioUIContext.Provider value={uiValue}>
          <HistoryContext.Provider value={historyValue}>
            {children}
          </HistoryContext.Provider>
        </ScenarioUIContext.Provider>
      </ScenarioActionsContext.Provider>
    </ScenarioDataContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
//  Hooks
/* ------------------------------------------------------------------ */
export function useScenario(): ScenarioContextValue {
  const data = useContext(ScenarioDataContext);
  const actions = useContext(ScenarioActionsContext);
  const ui = useContext(ScenarioUIContext);
  const history = useContext(HistoryContext);
  if (!data || !actions || !ui || !history) {
    throw new Error("useScenario must be inside ScenarioProvider");
  }
  return useMemo<ScenarioContextValue>(
    () => ({
      ...data,
      ...actions,
      ...ui,
      ...history,
    }),
    [data, actions, ui, history],
  );
}

export function useScenarioData(): ScenarioDataContextValue {
  const ctx = useContext(ScenarioDataContext);
  if (!ctx) throw new Error("useScenarioData must be inside ScenarioProvider");
  return ctx;
}

export function useScenarioActions(): ScenarioActionsContextValue {
  const ctx = useContext(ScenarioActionsContext);
  if (!ctx)
    throw new Error("useScenarioActions must be inside ScenarioProvider");
  return ctx;
}

export function useScenarioUI(): ScenarioUIContextValue {
  const ctx = useContext(ScenarioUIContext);
  if (!ctx) throw new Error("useScenarioUI must be inside ScenarioProvider");
  return ctx;
}

export function useHistory(): HistoryContextValue {
  const ctx = useContext(HistoryContext);
  if (!ctx) throw new Error("useHistory must be inside ScenarioProvider");
  return ctx;
}

// --- simple hash router ---
export function useHashRoute(): [string, (next: string) => void] {
  const [route, setRoute] = useState(
    () => globalThis.location.hash.replace(/^#/, "") || "/",
  );
  useEffect(() => {
    const onHash = () =>
      setRoute(globalThis.location.hash.replace(/^#/, "") || "/");
    globalThis.addEventListener("hashchange", onHash);
    globalThis.addEventListener("popstate", onHash);
    return () => {
      globalThis.removeEventListener("hashchange", onHash);
      globalThis.removeEventListener("popstate", onHash);
    };
  }, []);
  return [
    route,
    (next) => {
      history.pushState(null, "", "#" + next);
      setRoute(next);
    },
  ];
}
